/**
 * DokumentGeneratorWizard.jsx
 * ===========================
 * 7-trins wizard til at generere juridiske dokumenter fra sagsakter.
 * Erstatter AI-dokumentfabrik i KlientOversigt.jsx.
 *
 * Props:
 *   sagId        (number, required)  — sag-id
 *   sag_filer    (array, optional)   — forudindlæste filer fra parent
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../api';
import { T } from '../tema';

// ─── Dokumenttyper (matcher services/dokument-typer.js) ────────────────────
const DOKUMENT_TYPER = [
  { key: 'partshoring',        label: 'Partshøringssvar',           ikon: '📝' },
  { key: 'redegoerelse',       label: 'Redegørelse',                 ikon: '📄' },
  { key: 'indsigelse',         label: 'Indsigelse',                  ikon: '⚠️' },
  { key: 'klage',              label: 'Klage',                       ikon: '📮' },
  { key: 'tidslinje',          label: 'Kronologisk tidslinje',       ikon: '📅' },
  { key: 'samvaersbeskrivelse',label: 'Samværsbeskrivelse',          ikon: '👨‍👧' },
  { key: 'brev_kommune',       label: 'Brev til kommune',            ikon: '🏛️' },
  { key: 'brev_modpart',       label: 'Brev til modpart',            ikon: '✉️' },
  { key: 'opsummering',        label: 'Sagsopsummering',             ikon: '📋' },
  { key: 'moedeoplaeg',        label: 'Mødeoplæg',                   ikon: '🗂️' },
  { key: 'sagsopstart',        label: 'Sagsopstartsdokument',        ikon: '🚀' },
];

// Bilag-reference regex — tolerant parsing
const BILAG_REGEX = /\[Bilag\s+(\d+)(?:[,\s]+side\s+(\d+))?\]/gi;

function parse_bilag_refs(tekst) {
  const refs = [];
  let m;
  const r = new RegExp(BILAG_REGEX.source, 'gi');
  while ((m = r.exec(tekst)) !== null) {
    refs.push({ bilag_nr: parseInt(m[1], 10), side: m[2] ? parseInt(m[2], 10) : null, pos: m.index });
  }
  return refs;
}

// ─── Trin-indikator ─────────────────────────────────────────────────────────
function TrinIndikator({ aktiv, total }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{
          width: i + 1 === aktiv ? 28 : 10,
          height: 10,
          borderRadius: 5,
          background: i + 1 < aktiv ? T.blaa : i + 1 === aktiv ? T.blaa : '#94a3b8',
          opacity: i + 1 < aktiv ? 0.6 : 1,
          transition: 'all 0.3s',
        }} />
      ))}
    </div>
  );
}

// ─── Filindeks-badge ─────────────────────────────────────────────────────────
function IndeksBadge({ status }) {
  const cfg = {
    klar:              { label: 'Klar',          farve: '#10b981', bg: '#d1fae5' },
    under_indeksering: { label: 'Indekserer…',   farve: '#f59e0b', bg: '#fef3c7' },
    ikke_indekseret:   { label: 'Ikke indekseret', farve: '#6b7280', bg: '#f3f4f6' },
    fejlet:            { label: 'Fejlet',         farve: '#ef4444', bg: '#fee2e2' },
    ikke_understottet: { label: 'Ikke understøttet', farve: '#9ca3af', bg: '#f3f4f6' },
  };
  const c = cfg[status] || cfg.ikke_indekseret;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 10, color: c.farve, background: c.bg }}>
      {c.label}
    </span>
  );
}

// ─── Fremgangsbar ────────────────────────────────────────────────────────────
function Fremgangsbar({ behandlet, total, fase_detalje }) {
  const pct = total > 0 ? Math.round((behandlet / total) * 100) : null;
  return (
    <div>
      {pct !== null && (
        <div style={{ background: '#e2e8f0', borderRadius: 6, height: 8, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: T.blaa, borderRadius: 6, transition: 'width 0.5s' }} />
        </div>
      )}
      <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{fase_detalje || 'Behandler…'}</p>
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────────────────────
export default function DokumentGeneratorWizard({ sagId, sag_filer: prop_filer, haendelse_kontekst = null, modal_mode = false, onLuk = null }) {
  const [trin, setTrin]             = useState(1);
  const [filer, setFiler]           = useState(prop_filer || []);
  const [valgte_ids, setValgteIds]  = useState([]);
  const [dok_type, setDokType]      = useState('');
  const [klient_bem, setKlientBem]  = useState('');
  const [sbh_bem, setSbhBem]        = useState('');

  const [job, setJob]               = useState(null);
  const [job_id, setJobId]          = useState(null);
  const [starter, setStarter]       = useState(false);
  const [godkender, setGodkender]   = useState(false);
  const [resultat, setResultat]     = useState(null);

  const [udkast_tekst, setUdkastTekst] = useState('');
  const [bilag_kort, setBilagKort]     = useState([]);

  // Split-screen kilde
  const [vis_kilde, setVisKilde]       = useState(false);
  const [valgt_bilag_nr, setValgtBilagNr] = useState(null);
  const [blob_url, setBlobUrl]         = useState(null);
  const [blob_loading, setBlobLoading] = useState(false);

  // Fil-filter (trin 2)
  const [fil_filter, setFilFilter] = useState('alle');

  // Word-download (trin 6)
  const [word_downloading, setWordDownloading] = useState(false);

  // Tilret med ny instruktion (trin 6)
  const [ny_instruktion, setNyInstruktion] = useState('');
  const [regenererer, setRegenererer]      = useState(false);

  const poller_ref = useRef(null);

  // Pre-fill fra haendelse_kontekst
  useEffect(() => {
    if (haendelse_kontekst) {
      if (haendelse_kontekst.dok_type) setDokType(haendelse_kontekst.dok_type);
      if (haendelse_kontekst.forud_fil_ids && haendelse_kontekst.forud_fil_ids.length) setValgteIds(haendelse_kontekst.forud_fil_ids);
      if (haendelse_kontekst.sbh_bemaerkning) setSbhBem(haendelse_kontekst.sbh_bemaerkning);
    }
  }, [haendelse_kontekst]);

  // ── Hent aktiv sags-filer (opdatér indeks-status) ───────────────────────
  const hent_filer = useCallback(async () => {
    try {
      const data = await api(`/filer/sag/${sagId}`, 'GET');
      if (data?.filer && Array.isArray(data.filer)) setFiler(data.filer);
    } catch (_) {}
  }, [sagId]);

  // ── Poll job-status ──────────────────────────────────────────────────────
  const poll_status = useCallback(async (jid) => {
    if (!jid) return;
    try {
      const data = await api(`/dokumentgenerator/status/${jid}`, 'GET');
      setJob(data);
      if (data.udkast_tekst && !udkast_tekst) setUdkastTekst(data.udkast_tekst);
      if (data.bilag_kort) setBilagKort(data.bilag_kort);
      if (['klar', 'fejlet'].includes(data.status)) {
        clearInterval(poller_ref.current);
        poller_ref.current = null;
        if (data.status === 'klar') {
          setTrin(6);
          hent_filer();
        }
      }
    } catch (_) {}
  }, [udkast_tekst, hent_filer]);

  // ── Genoptag evt. aktivt job ved mount ──────────────────────────────────
  useEffect(() => {
    hent_filer();
    (async () => {
      try {
        const data = await api(`/dokumentgenerator/aktivt-job/${sagId}`, 'GET');
        if (data?.job) {
          const j = data.job;
          setJob(j);
          setJobId(j.id);
          if (j.bilag_kort) setBilagKort(j.bilag_kort);
          if (j.udkast_tekst) setUdkastTekst(j.udkast_tekst);
          if (j.status === 'klar') setTrin(6);
          else if (j.status === 'fejlet') setTrin(5);
          else setTrin(5); // Under behandling
        }
      } catch (_) {}
    })();
    return () => {
      clearInterval(poller_ref.current);
      if (blob_url) URL.revokeObjectURL(blob_url);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sagId]);

  // ── Start polling når job_id sættes og job kører ────────────────────────
  useEffect(() => {
    if (job_id && job && !['klar', 'fejlet', 'annulleret'].includes(job.status)) {
      clearInterval(poller_ref.current);
      poller_ref.current = setInterval(() => poll_status(job_id), 2000);
    }
    return () => { clearInterval(poller_ref.current); };
  }, [job_id, job?.status, poll_status]);

  // ── Filvalg-toggle ───────────────────────────────────────────────────────
  const toggle_fil = useCallback((fil_id) => {
    setValgteIds(prev =>
      prev.includes(fil_id) ? prev.filter(id => id !== fil_id) : [...prev, fil_id]
    );
  }, []);

  // ── Start job ────────────────────────────────────────────────────────────
  const start_job = async () => {
    setStarter(true);
    try {
      const data = await api('/dokumentgenerator/start', 'POST', {
        sag_id: sagId,
        dokument_type: dok_type,
        valgte_fil_ids: valgte_ids,
        klient_bemaerkninger: klient_bem || null,
        sagsbehandler_bemaerkninger: sbh_bem || null,
        haendelse_id: haendelse_kontekst ? haendelse_kontekst.haendelse_id : null,
      });
      setJobId(data.job_id);
      setBilagKort(data.bilag_kort || []);
      // Poll én gang straks
      const j_data = await api(`/dokumentgenerator/status/${data.job_id}`, 'GET');
      setJob(j_data);
      setTrin(5);
    } catch (e) {
      alert('Kunne ikke starte dokumentgenerering: ' + (e.message || 'Ukendt fejl'));
    } finally {
      setStarter(false);
    }
  };

  // ── Genstart fejlet job ──────────────────────────────────────────────────
  const genstart_job = async () => {
    if (!job_id) return;
    setStarter(true);
    try {
      const data = await api('/dokumentgenerator/start', 'POST', {
        sag_id: sagId,
        dokument_type: job?.dokument_type || dok_type,
        valgte_fil_ids: job?.valgte_fil_ids || valgte_ids,
        genstart_job_id: job_id,
      });
      const j_data = await api(`/dokumentgenerator/status/${data.job_id}`, 'GET');
      setJob(j_data);
    } catch (e) {
      alert('Genstart fejlede: ' + (e.message || 'Ukendt fejl'));
    } finally {
      setStarter(false);
    }
  };

  // ── Godkend og gem ───────────────────────────────────────────────────────
  const godkend = async () => {
    setGodkender(true);
    try {
      const data = await api('/dokumentgenerator/godkend', 'POST', {
        job_id,
        udkast_tekst,
      });
      setResultat(data);
      setTrin(7);
    } catch (e) {
      alert('Kunne ikke gemme dokument: ' + (e.message || 'Ukendt fejl'));
    } finally {
      setGodkender(false);
    }
  };

  // ── Nulstil til AI-forslag ───────────────────────────────────────────────
  const nulstil_til_ai = () => {
    if (job?.original_udkast_tekst && window.confirm('Nulstil til AI-forslag? Dine ændringer mistes.')) {
      setUdkastTekst(job.original_udkast_tekst);
    }
  };

  // ── Download DOCX ────────────────────────────────────────────────────────
  const download_docx = async () => {
    setWordDownloading(true);
    try {
      const data = await api('/dokumentgenerator/download-docx', 'POST', {
        job_id,
        udkast_tekst,
      });
      if (data?.docx_base64) {
        const bytes = Uint8Array.from(atob(data.docx_base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filnavn || 'SV_dokument.docx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      alert('Kunne ikke downloade Word-dokument: ' + (e.message || 'Ukendt fejl'));
    } finally {
      setWordDownloading(false);
    }
  };

  // ── Tilret udkast med ny instruktion ────────────────────────────────────
  const regenerer_med_instruktion = async () => {
    if (!ny_instruktion.trim()) return;
    setRegenererer(true);
    try {
      const data = await api('/dokumentgenerator/regenerer', 'POST', {
        job_id,
        ny_instruktion: ny_instruktion.trim(),
      });
      if (data?.udkast_tekst) {
        setUdkastTekst(data.udkast_tekst);
        setNyInstruktion('');
      }
    } catch (e) {
      alert('Tilretning fejlede: ' + (e.message || 'Ukendt fejl'));
    } finally {
      setRegenererer(false);
    }
  };

  // ── Ny wizard (nulstil al state) ─────────────────────────────────────────
  const ny_wizard = () => {
    clearInterval(poller_ref.current);
    setTrin(1); setJob(null); setJobId(null);
    setValgteIds([]); setDokType(''); setKlientBem(''); setSbhBem('');
    setUdkastTekst(''); setBilagKort([]); setResultat(null);
    setBlobUrl(null); setValgtBilagNr(null); setVisKilde(false);
  };

  // ── Indlæs fil til iframe (split-screen) ─────────────────────────────────
  const indlæs_bilag = async (bilag_nr) => {
    const bilag = bilag_kort.find(b => b.bilag_nr === bilag_nr);
    if (!bilag) return;
    const fil = filer.find(f => f.id === bilag.fil_id);
    if (!fil?.download_url) return;
    setBlobLoading(true);
    setValgtBilagNr(bilag_nr);
    if (blob_url) URL.revokeObjectURL(blob_url);
    try {
      const res = await fetch(fil.download_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch (_) {
      setBlobUrl(null);
    } finally {
      setBlobLoading(false);
    }
  };

  // ── Bilag-refs i teksten ─────────────────────────────────────────────────
  const bilag_refs = useMemo(() => {
    if (!udkast_tekst) return [];
    const refs = parse_bilag_refs(udkast_tekst);
    const unikke = [];
    const set = new Set();
    for (const r of refs) {
      if (!set.has(r.bilag_nr)) { set.add(r.bilag_nr); unikke.push(r); }
    }
    return unikke;
  }, [udkast_tekst]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render hjælpere
  // ─────────────────────────────────────────────────────────────────────────
  const kort_stil = {
    background: '#ffffff',
    boxShadow: '0 8px 40px rgba(0,0,0,0.22), 0 0 0 1px rgba(15,23,42,0.06)',
    borderRadius: 16,
    padding: 28,
    maxWidth: 700,
    margin: '0 auto',
  };

  const btn_primary = {
    background: T.blaa || '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 22px',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  };

  const btn_ghost = {
    background: 'transparent',
    border: '1px solid rgba(15,23,42,0.15)',
    borderRadius: 10,
    padding: '10px 18px',
    fontWeight: 500,
    fontSize: 14,
    cursor: 'pointer',
    color: '#374151',
  };

  const overskrift = {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 6,
    color: '#1e293b',
  };

  const under_overskrift = {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 24,
    marginTop: 0,
  };

  // ─────────────────────────────────────────────────────────────────────────
  // TRIN 1 — Vælg dokumenttype
  // ─────────────────────────────────────────────────────────────────────────
  if (trin === 1) return (
    <div style={kort_stil}>
      <TrinIndikator aktiv={1} total={7} />
      <h3 style={overskrift}>Vælg dokumenttype</h3>
      <p style={under_overskrift}>Hvilken type dokument skal AI'en hjælpe med at generere?</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 24 }}>
        {DOKUMENT_TYPER.map(dt => (
          <button
            key={dt.key}
            onClick={() => setDokType(dt.key)}
            style={{
              ...btn_ghost,
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: dok_type === dt.key ? 'rgba(79,70,229,0.10)' : 'rgba(15,23,42,0.02)',
              border: dok_type === dt.key ? `2px solid ${T.blaa}` : '1px solid rgba(15,23,42,0.12)',
              color: dok_type === dt.key ? T.blaa : '#1e293b',
            }}
          >
            <span style={{ fontSize: 18 }}>{dt.ikon}</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{dt.label}</span>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          style={{ ...btn_primary, opacity: !dok_type ? 0.5 : 1 }}
          disabled={!dok_type}
          onClick={() => setTrin(2)}
        >
          Næste →
        </button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TRIN 2 — Vælg filer
  // ─────────────────────────────────────────────────────────────────────────
  if (trin === 2) {
    // Alle understøttede filer — sorteret nyeste først
    const understottede_alle = [...filer.filter(f =>
      (f.mime_type || '').includes('pdf') ||
      (f.mime_type || '').includes('word') ||
      (f.original_navn || '').match(/\.(pdf|docx|doc|txt|eml)$/i)
    )].sort((a, b) => new Date(b.uploadet_kl || 0) - new Date(a.uploadet_kl || 0));

    // Anvend aktiv filter
    const fil_visning = (() => {
      if (fil_filter === 'pdf')   return understottede_alle.filter(f => (f.mime_type || '').includes('pdf')  || (f.original_navn || '').match(/\.pdf$/i));
      if (fil_filter === 'word')  return understottede_alle.filter(f => (f.mime_type || '').includes('word') || (f.original_navn || '').match(/\.(docx|doc)$/i));
      if (fil_filter === 'eml')   return understottede_alle.filter(f => (f.original_navn || '').match(/\.eml$/i));
      if (fil_filter === 'andre') return understottede_alle.filter(f =>
        !(f.mime_type || '').includes('pdf') &&
        !(f.mime_type || '').includes('word') &&
        !(f.original_navn || '').match(/\.(pdf|docx|doc|eml)$/i)
      );
      return understottede_alle; // 'alle'
    })();

    const FILTER_PILLS = [
      { key: 'alle',  label: 'Alle' },
      { key: 'pdf',   label: 'PDF' },
      { key: 'word',  label: 'Word' },
      { key: 'eml',   label: 'EML' },
      { key: 'andre', label: 'Andre' },
    ];

    return (
      <div style={kort_stil}>
        <TrinIndikator aktiv={2} total={7} />
        <h3 style={overskrift}>Vælg filer til analyse</h3>
        <p style={under_overskrift}>
          Markér de filer AI'en skal læse. Rækkefølgen bliver bilagsnumrering (Bilag 1, 2, 3…). Sorteret nyeste først.
        </p>

        {/* Filter-pills */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {FILTER_PILLS.map(p => (
            <button
              key={p.key}
              onClick={() => setFilFilter(p.key)}
              style={{
                ...btn_ghost,
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: fil_filter === p.key ? 700 : 500,
                background: fil_filter === p.key ? 'rgba(79,70,229,0.10)' : 'transparent',
                border: `1px solid ${fil_filter === p.key ? T.blaa : 'rgba(15,23,42,0.12)'}`,
                color: fil_filter === p.key ? T.blaa : '#374151',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {understottede_alle.length === 0 ? (
          <p style={{ color: '#ef4444', fontSize: 14 }}>Ingen understøttede filer fundet på sagen (PDF, DOCX, TXT, EML).</p>
        ) : fil_visning.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: 14 }}>Ingen filer matcher det valgte filter.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {fil_visning.map((f) => {
              const valgt = valgte_ids.includes(f.id);
              const bilag_nr = valgte_ids.indexOf(f.id) + 1;
              const dato_vis = f.uploadet_kl
                ? new Date(f.uploadet_kl).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : null;
              return (
                <div
                  key={f.id}
                  onClick={() => toggle_fil(f.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: `${valgt ? 2 : 1}px solid ${valgt ? T.blaa : 'rgba(15,23,42,0.12)'}`,
                    background: valgt ? 'rgba(79,70,229,0.06)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: 6,
                    border: `2px solid ${valgt ? T.blaa : '#d1d5db'}`,
                    background: valgt ? T.blaa : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {valgt && <span style={{ color: '#fff', fontSize: 14, lineHeight: 1 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {valgt && <span style={{ color: T.blaa, marginRight: 6 }}>Bilag {bilag_nr}</span>}
                      {f.visningsnavn || f.original_navn}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                      {dato_vis && (
                        <span style={{ fontSize: 11, color: '#64748b' }}>{dato_vis}</span>
                      )}
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {f.størrelse ? `${(f.størrelse / 1024).toFixed(0)} KB` : ''}
                      </span>
                      <IndeksBadge status={f.fuld_indeks_status || 'ikke_indekseret'} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button style={btn_ghost} onClick={() => setTrin(1)}>← Tilbage</button>
          <button
            style={{ ...btn_primary, opacity: valgte_ids.length === 0 ? 0.5 : 1 }}
            disabled={valgte_ids.length === 0}
            onClick={() => setTrin(3)}
          >
            {valgte_ids.length > 0 ? `${valgte_ids.length} fil${valgte_ids.length > 1 ? 'er' : ''} valgt` : 'Vælg filer'} →
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TRIN 3 — Bemærkninger
  // ─────────────────────────────────────────────────────────────────────────
  if (trin === 3) return (
    <div style={kort_stil}>
      <TrinIndikator aktiv={3} total={7} />
      <h3 style={overskrift}>Tilføj bemærkninger</h3>
      <p style={under_overskrift}>Valgfrit — men hjælper AI'en med at fokusere korrekt.</p>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
          Klientens egne bemærkninger
        </label>
        <textarea
          value={klient_bem}
          onChange={e => setKlientBem(e.target.value)}
          placeholder="Hvad ønsker klienten at fremhæve? Særlige hensyn?"
          style={{ width: '100%', minHeight: 90, padding: 10, borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
          Sagsbehandlerens bemærkninger
        </label>
        <textarea
          value={sbh_bem}
          onChange={e => setSbhBem(e.target.value)}
          placeholder="Særlige juridiske hensyn, tone, fokuspunkter..."
          style={{ width: '100%', minHeight: 90, padding: 10, borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button style={btn_ghost} onClick={() => setTrin(2)}>← Tilbage</button>
        <button style={btn_primary} onClick={() => setTrin(4)}>Næste →</button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TRIN 4 — Bekræft og start
  // ─────────────────────────────────────────────────────────────────────────
  if (trin === 4) {
    const dok_type_label = DOKUMENT_TYPER.find(d => d.key === dok_type)?.label || dok_type;
    const valgte_filer_info = filer.filter(f => valgte_ids.includes(f.id));
    return (
      <div style={kort_stil}>
        <TrinIndikator aktiv={4} total={7} />
        <h3 style={overskrift}>Bekræft og start</h3>
        <p style={under_overskrift}>Gennemgå valget inden AI'en starter.</p>
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 24 }}>
          <div style={{ marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>DOKUMENTTYPE</span>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 2 }}>{dok_type_label}</div>
          </div>
          <div>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>VALGTE FILER ({valgte_ids.length})</span>
            {valgte_filer_info.map((f, i) => (
              <div key={f.id} style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                <span style={{ color: T.blaa, fontWeight: 600, marginRight: 6 }}>Bilag {i + 1}</span>
                {f.visningsnavn || f.original_navn}
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10, padding: 12, marginBottom: 24, fontSize: 13, color: '#92400e' }}>
          AI-genereringen tager typisk 1-5 minutter afhængigt af filernes størrelse.
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button style={btn_ghost} onClick={() => setTrin(3)} disabled={starter}>← Tilbage</button>
          <button
            style={{ ...btn_primary, opacity: starter ? 0.7 : 1 }}
            disabled={starter}
            onClick={start_job}
          >
            {starter ? 'Starter…' : 'Start AI-generering'}
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TRIN 5 — Progress / Fejl
  // ─────────────────────────────────────────────────────────────────────────
  if (trin === 5) {
    const fejlet = job?.status === 'fejlet';
    const i_koe = job?.status === 'afventer' || !job?.status;
    const i_gang = job?.status === 'i_gang';

    let titel = 'AI genererer dokument…';
    if (fejlet) titel = 'Generering fejlede';
    else if (i_koe) titel = 'Dokument sendt til behandling';

    return (
      <div style={kort_stil}>
        <TrinIndikator aktiv={5} total={7} />
        <h3 style={overskrift}>{titel}</h3>

        {fejlet ? (
          <div>
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: '#991b1b' }}>
              <strong>Fejl:</strong> {job?.fejlbesked || 'Ukendt fejl'}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={btn_ghost} onClick={ny_wizard}>Start forfra</button>
              <button style={{ ...btn_primary, opacity: starter ? 0.7 : 1 }} disabled={starter} onClick={genstart_job}>
                {starter ? 'Genstarter…' : 'Prøv igen'}
              </button>
            </div>
          </div>
        ) : i_koe ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                border: '5px solid #e2e8f0',
                borderTopColor: T.blaa,
                animation: 'spin 1.4s linear infinite',
              }} />
            </div>
            <div style={{ background: 'rgba(79,70,229,0.06)', border: '1px solid rgba(79,70,229,0.15)', borderRadius: 10, padding: '14px 18px', marginBottom: 12 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1e293b' }}>Dokumentet er i kø til AI-behandling</p>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                AI-assistenten behandler dokumenter løbende. Estimeret ventetid: <strong>op til 5 minutter</strong>.
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#94a3b8' }}>
                Du kan lukke dette vindue — dokumentet er gemt og du finder det igen via knappen "Generer dokument".
              </p>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                border: `4px solid ${T.skillelinje || '#e2e8f0'}`,
                borderTopColor: T.blaa,
                animation: 'spin 0.8s linear infinite',
              }} />
            </div>
            <Fremgangsbar
              behandlet={job?.fremgang_bidder_behandlet || 0}
              total={job?.fremgang_bidder_total || 0}
              fase_detalje={job?.fase_detalje || 'AI skriver dokument…'}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TRIN 6 — Gennemgå og redigér (split-screen)
  // ─────────────────────────────────────────────────────────────────────────
  if (trin === 6) {
    const BREAK = 1100;
    const er_bred = window.innerWidth >= BREAK;
    const usikre_bilag = bilag_kort.filter(b => b.usikker_udtraek);
    const dok_type_label = DOKUMENT_TYPER.find(d => d.key === (job?.dokument_type || dok_type))?.label || 'Udkast';

    return (
      <div style={{ maxWidth: er_bred ? 1300 : 760, margin: '0 auto', background: '#ffffff', borderRadius: 16, padding: 24, boxShadow: '0 8px 40px rgba(0,0,0,0.22), 0 0 0 1px rgba(15,23,42,0.06)' }}>
        {/* Header med kontrolknapper */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h3 style={{ ...overskrift, margin: 0 }}>{dok_type_label} — udkast klar</h3>
            <p style={{ ...under_overskrift, margin: 0, marginTop: 2 }}>Gennemgå og redigér inden du gemmer</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={btn_ghost} onClick={() => setVisKilde(!vis_kilde)}>
              {vis_kilde ? 'Skjul kilder' : 'Vis med kilder'}
            </button>
            {job?.original_udkast_tekst && (
              <button style={btn_ghost} onClick={nulstil_til_ai}>Nulstil til AI-forslag</button>
            )}
            <button
              style={{ ...btn_ghost, opacity: word_downloading ? 0.7 : 1 }}
              disabled={word_downloading}
              onClick={download_docx}
              title="Download udkastet som Word-fil"
            >
              {word_downloading ? 'Downloader…' : 'Åbn i Word'}
            </button>
            <button
              style={{ ...btn_primary, opacity: godkender ? 0.7 : 1 }}
              disabled={godkender}
              onClick={() => setTrin(7)}
            >
              Godkend →
            </button>
          </div>
        </div>

        {/* Usikre-advarsel */}
        {usikre_bilag.length > 0 && (
          <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
            ⚠ <strong>Bilag {usikre_bilag.map(b => b.bilag_nr).join(', ')}</strong> indeholdt svært-læselige sider (skannet dokument). Verificér disse referencer ekstra nøje.
          </div>
        )}

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexDirection: er_bred && vis_kilde ? 'row' : 'column' }}>
          {/* Venstre: teksteditor */}
          <div style={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
            {/* Bilag-navigation til kildevisning */}
            {bilag_refs.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>Hop til bilag:</span>
                {bilag_refs.map(r => (
                  <button
                    key={r.bilag_nr}
                    onClick={() => { setVisKilde(true); setValgtBilagNr(r.bilag_nr); indlæs_bilag(r.bilag_nr); }}
                    style={{
                      ...btn_ghost,
                      padding: '4px 10px',
                      fontSize: 12,
                      background: valgt_bilag_nr === r.bilag_nr ? 'rgba(79,70,229,0.08)' : 'transparent',
                      border: `1px solid ${valgt_bilag_nr === r.bilag_nr ? T.blaa : 'rgba(15,23,42,0.12)'}`,
                      color: valgt_bilag_nr === r.bilag_nr ? T.blaa : '#374151',
                    }}
                  >
                    {bilag_kort.find(b => b.bilag_nr === r.bilag_nr)?.usikker_udtraek ? '⚠ ' : ''}
                    Bilag {r.bilag_nr}
                  </button>
                ))}
              </div>
            )}
            <textarea
              value={udkast_tekst}
              onChange={e => setUdkastTekst(e.target.value)}
              style={{
                width: '100%',
                minHeight: er_bred && vis_kilde ? 600 : 420,
                padding: 16,
                borderRadius: 10,
                border: '1px solid rgba(15,23,42,0.12)',
                fontSize: 13.5,
                lineHeight: 1.7,
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box',
                background: '#fafafa',
              }}
            />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, textAlign: 'right' }}>
              {udkast_tekst.length.toLocaleString('da-DK')} tegn
            </div>

            {/* Tilret-panel */}
            <div style={{ marginTop: 14, padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid rgba(15,23,42,0.08)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>
                TILRET MED INSTRUKTION
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={ny_instruktion}
                  onChange={e => setNyInstruktion(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !regenererer && regenerer_med_instruktion()}
                  placeholder="Fx: Gør indledningen skarpere og tilføj dato for afgørelsen..."
                  disabled={regenererer}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid rgba(15,23,42,0.15)',
                    fontSize: 13,
                    background: regenererer ? '#f1f5f9' : '#fff',
                    color: '#1e293b',
                  }}
                />
                <button
                  onClick={regenerer_med_instruktion}
                  disabled={regenererer || !ny_instruktion.trim()}
                  style={{
                    ...btn_primary,
                    opacity: (regenererer || !ny_instruktion.trim()) ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {regenererer ? (
                    <>
                      <span style={{
                        display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff',
                        animation: 'spin 0.8s linear infinite',
                      }} />
                      Tilretter…
                    </>
                  ) : 'Tilret'}
                </button>
              </div>
            </div>
          </div>

          {/* Højre: kildedokument (kun ved vis_kilde) */}
          {vis_kilde && (
            <div style={{ flex: '0 0 auto', width: er_bred ? 500 : '100%' }}>
              <div style={{ marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {bilag_kort.map(b => (
                  <button
                    key={b.bilag_nr}
                    onClick={() => { setValgtBilagNr(b.bilag_nr); indlæs_bilag(b.bilag_nr); }}
                    style={{
                      ...btn_ghost,
                      padding: '5px 12px',
                      fontSize: 12,
                      background: valgt_bilag_nr === b.bilag_nr ? 'rgba(79,70,229,0.08)' : 'transparent',
                      border: `1px solid ${valgt_bilag_nr === b.bilag_nr ? T.blaa : 'rgba(15,23,42,0.12)'}`,
                      color: valgt_bilag_nr === b.bilag_nr ? T.blaa : '#374151',
                    }}
                  >
                    {b.usikker_udtraek ? '⚠ ' : ''}Bilag {b.bilag_nr}
                  </button>
                ))}
              </div>
              <div style={{ border: '1px solid rgba(15,23,42,0.12)', borderRadius: 10, overflow: 'hidden', height: er_bred ? 620 : 400, background: '#f1f5f9' }}>
                {blob_loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b', fontSize: 13 }}>
                    Indlæser dokument…
                  </div>
                ) : blob_url ? (
                  <iframe src={blob_url} title={`Bilag ${valgt_bilag_nr}`} style={{ width: '100%', height: '100%', border: 'none' }} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b', fontSize: 13 }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                    Klik et bilag ovenfor for at se dokumentet
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TRIN 7 — Bekræft og gem
  // ─────────────────────────────────────────────────────────────────────────
  if (trin === 7) {
    // Vis resultat hvis allerede gemt
    if (resultat?.succes) return (
      <div style={kort_stil}>
        <TrinIndikator aktiv={7} total={7} />
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
          <h3 style={overskrift}>Dokument gemt!</h3>
          <p style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>
            <strong>{resultat.filnavn}</strong> er {resultat.har_pdf ? 'konverteret til PDF og ' : ''}gemt på sagen.
          </p>
          {resultat.onedrive_sti && (
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>{resultat.onedrive_sti}</p>
          )}
          <button style={btn_primary} onClick={ny_wizard}>Generér nyt dokument</button>
        </div>
      </div>
    );

    const dok_type_label = DOKUMENT_TYPER.find(d => d.key === (job?.dokument_type || dok_type))?.label || 'Dokument';
    return (
      <div style={kort_stil}>
        <TrinIndikator aktiv={7} total={7} />
        <h3 style={overskrift}>Gem dokument</h3>
        <p style={under_overskrift}>Dokumentet gemmes på sagen og uploades til OneDrive.</p>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14, marginBottom: 24, fontSize: 13 }}>
          <strong>{dok_type_label}</strong> — {udkast_tekst.length.toLocaleString('da-DK')} tegn redigeret tekst
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button style={btn_ghost} onClick={() => setTrin(6)} disabled={godkender}>← Redigér mere</button>
          <button
            style={{ ...btn_primary, opacity: godkender ? 0.7 : 1 }}
            disabled={godkender}
            onClick={godkend}
          >
            {godkender ? 'Gemmer…' : 'Gem endeligt dokument'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
